/**
 * Loading of the orders per status: one request for every expanded block (or
 * one block), the pages kept per status, a 30 s polling that skips while a load
 * is running, and an in-flight request dropped when a new full load starts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Platform } from '@/shared/constants';
import type { StoreId } from '@/shared/config/app-config';
import { queryOrders, type OrderPage } from './api';
import {
  DEFAULT_PAGINATION,
  enrichOrder,
  sortOrders,
  type EnrichedOrder,
  type OrderPagination,
  type OrderSort,
} from './logic';

export const REFRESH_INTERVAL_MS = 30_000;

export interface OrdersState {
  customizations: Record<number, EnrichedOrder[]>;
  pagination: Record<number, OrderPagination>;
  isContentLoaded: boolean;
  isLoadingItems: boolean;
}

/** The loaded pages merged into the state (a full load starts from empty blocks). */
export const mergeLoadedOrders = (
  current: OrdersState,
  data: Record<string, OrderPage>,
  opened: readonly number[],
  fullLoad: boolean,
  { sort }: { sort: OrderSort },
): OrdersState => {
  const customizations: Record<number, EnrichedOrder[]> = fullLoad
    ? {}
    : { ...current.customizations };
  const pagination: Record<number, OrderPagination> = {
    ...current.pagination,
  };
  for (const status of opened) customizations[status] = [];
  for (const [key, page] of Object.entries(data)) {
    pagination[Number(key)] = { ...page, docs: [] };
    for (const doc of page.docs) {
      const block = (customizations[doc.customisationStatusId] ??= []);
      block.push(enrichOrder(doc));
    }
  }
  for (const status of opened) {
    const block = customizations[status] ?? [];
    if (block.length === 0) pagination[status] = DEFAULT_PAGINATION;
    customizations[status] = sortOrders(block, sort);
  }
  return {
    customizations,
    pagination,
    isContentLoaded: true,
    isLoadingItems: false,
  };
};

export interface UseOrdersOptions {
  platform: Platform;
  enabled: boolean;
  /** Status ids of the blocks currently expanded (the "all" load targets them). */
  openedStatuses: () => number[];
  storeIds: () => readonly StoreId[];
  search: () => string;
  sort: () => OrderSort;
  onLoaded?: () => void;
}

export const useOrders = ({
  platform,
  enabled,
  openedStatuses,
  storeIds,
  search,
  sort,
  onLoaded,
}: UseOrdersOptions) => {
  const [state, setState] = useState<OrdersState>({
    customizations: {},
    pagination: {},
    isContentLoaded: false,
    isLoadingItems: false,
  });
  const stateRef = useRef(state);
  const inFlight = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const options = useRef({ openedStatuses, storeIds, search, sort, onLoaded });
  useEffect(() => {
    stateRef.current = state;
    options.current = { openedStatuses, storeIds, search, sort, onLoaded };
  });

  const setPage = useCallback((status: number, page: number) => {
    setState((current) => ({
      ...current,
      pagination: {
        ...current.pagination,
        [status]: {
          ...(current.pagination[status] ?? DEFAULT_PAGINATION),
          page,
          changingPage: true,
        },
      },
    }));
  }, []);

  const loadItemsRef = useRef<(status?: number | 'all') => Promise<void>>(
    async () => {},
  );

  const restartPolling = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (!stateRef.current.isLoadingItems) void loadItemsRef.current('all');
    }, REFRESH_INTERVAL_MS);
  }, []);

  const loadItems = useCallback(
    async (status: number | 'all' = 'all') => {
      if (!enabled) return;
      restartPolling();
      if (status === 'all') inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      setState((current) => ({ ...current, isLoadingItems: true }));

      const opened =
        status === 'all' ? options.current.openedStatuses() : [status];
      const pagination = stateRef.current.pagination;
      try {
        const data = await queryOrders({
          statuses: opened,
          pages: opened.map((s) => pagination[s]?.page ?? 1),
          storeIds: options.current.storeIds(),
          platform,
          search: options.current.search().toLowerCase(),
        });
        if (controller.signal.aborted) return;
        setState((current) =>
          mergeLoadedOrders(current, data, opened, status === 'all', {
            sort: options.current.sort(),
          }),
        );
        options.current.onLoaded?.();
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error(error);
        setState((current) => ({ ...current, isLoadingItems: false }));
      }
    },
    [enabled, platform, restartPolling],
  );
  useEffect(() => {
    loadItemsRef.current = loadItems;
  }, [loadItems]);

  const stopPolling = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  /** Re-sorts the loaded blocks in place. */
  const resort = useCallback((nextSort: OrderSort) => {
    setState((current) => ({
      ...current,
      customizations: Object.fromEntries(
        Object.entries(current.customizations).map(([key, items]) => [
          key,
          sortOrders(items, nextSort),
        ]),
      ),
    }));
  }, []);

  const resetPages = useCallback(() => {
    setState((current) => ({
      ...current,
      pagination: Object.fromEntries(
        Object.keys(current.pagination).map((key) => [key, DEFAULT_PAGINATION]),
      ),
    }));
  }, []);

  return {
    ...state,
    loadItems,
    setPage,
    resort,
    resetPages,
    restartPolling,
    stopPolling,
    setIsLoading: (isLoading: boolean) =>
      setState((current) => ({ ...current, isLoadingItems: isLoading })),
  };
};
